import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../context/AuthContext'
import { API_FILE_URL } from '../constants/api'

// ── Helpers ───────────────────────────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_URL || API_FILE_URL

const fmt     = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
const fmtTime = (d) => d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'
const num     = (n) => n != null ? Number(n).toLocaleString() : '0'

const parseThumb = (thumbnail) => {
  if (!thumbnail) return null
  try {
    const arr = typeof thumbnail === 'string' && thumbnail.startsWith('[')
      ? JSON.parse(thumbnail) : null
    const raw = arr ? arr[0] : thumbnail
    if (!raw) return null
    const url = String(raw).trim()
    if (url.startsWith('http://') || url.startsWith('https://')) return url
    const filename = url.replace(/\\/g, '/').split('/').pop()
    return `${API_BASE}/${filename}`
  } catch { return null }
}

const STATUS_MAP = {
  1: { bg: '#f0fdf4', color: '#15803d', dot: '#16a34a', label: 'Active'  },
  0: { bg: '#fef2f2', color: '#b91c1c', dot: '#ef4444', label: 'Hidden'  },
  2: { bg: '#fffbeb', color: '#b45309', dot: '#f59e0b', label: 'Pending' },
}
const statusStyle = (s) => STATUS_MAP[s] ?? STATUS_MAP[Number(s)] ?? { bg: '#f3f4f6', color: '#6b7280', dot: '#9ca3af', label: '—' }

const TIPS = [
  'Show the product from multiple angles',
  'Keep it punchy — first 3 seconds matter most',
  'Tag products so viewers can buy instantly',
  'Good lighting = significantly more sales',
]

// ── Sub-components ────────────────────────────────────────────────────────────
function StatCard({ label, value, accent }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '18px 20px', flex: 1 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.6px', color: accent || '#111317' }}>{value}</div>
    </div>
  )
}

function StatusBadge({ status }) {
  const s = statusStyle(status)
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: s.bg, color: s.color, fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", padding: '3px 8px', borderRadius: 4, textTransform: 'uppercase' }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.dot }} />{s.label}
    </span>
  )
}

// ── Delete Confirm Modal ──────────────────────────────────────────────────────
function DeleteModal({ reel, onClose, onConfirm, loading }) {
  if (!reel) return null
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 400 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 400, background: '#fff', borderRadius: 14, zIndex: 401, boxShadow: '0 20px 60px rgba(0,0,0,0.15)', animation: 'popIn 0.2s ease' }}>
        <style>{`@keyframes popIn { from { opacity:0; transform:translate(-50%,-48%) scale(0.97) } to { opacity:1; transform:translate(-50%,-50%) scale(1) } }`}</style>
        <div style={{ padding: '28px 28px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🗑️</div>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>Delete Clip?</div>
          <div style={{ fontSize: 13, color: '#9a9a94', lineHeight: 1.7 }}>
            "<b>{reel.title || reel.caption || `Clip #${reel.reel_id || reel.reels_id}`}</b>" will be permanently deleted. This cannot be undone.
          </div>
        </div>
        <div style={{ padding: '0 28px 24px', display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, height: 42, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
          <button onClick={onConfirm} disabled={loading} style={{ flex: 1, height: 42, borderRadius: 8, border: 'none', background: loading ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            {loading ? 'Deleting…' : 'Yes, Delete'}
          </button>
        </div>
      </div>
    </>
  )
}

// ── Product Picker Portal — renders at root level so position:fixed works ─────
function ProductPickerPortal({ visible, products, tagged, loadingProds, pickerSearch, setPickerSearch, onToggle, onClose }) {
  if (!visible) return null
  const filtered = products.filter(p => p.name.toLowerCase().includes(pickerSearch.toLowerCase()))
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 600 }} />
      <div style={{ position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: 480, maxHeight: '75vh', background: '#fff', borderRadius: '24px 24px 0 0', zIndex: 601, display: 'flex', flexDirection: 'column', boxShadow: '0 -6px 40px rgba(0,0,0,0.15)' }}>
        <div style={{ width: 40, height: 4, borderRadius: 2, background: '#e9e9e4', margin: '12px auto 4px' }} />
        <div style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f3f3f0' }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800 }}>Tag Products</div>
            <div style={{ fontSize: 12, color: '#9a9a94', marginTop: 2 }}>
              {tagged.length === 0 ? 'Viewers can tap to buy instantly' : `${tagged.length} product${tagged.length > 1 ? 's' : ''} tagged`}
            </div>
          </div>
          <button onClick={onClose} style={{ height: 36, padding: '0 18px', borderRadius: 10, border: 'none', background: '#7c3aed', color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Done</button>
        </div>
        <div style={{ padding: '12px 20px', borderBottom: '1px solid #f3f3f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f6f6f3', border: '1px solid #e9e9e4', borderRadius: 10, padding: '8px 12px' }}>
            <span style={{ fontSize: 14, color: '#9a9a94' }}>🔍</span>
            <input value={pickerSearch} onChange={e => setPickerSearch(e.target.value)} placeholder="Search your products…" style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, flex: 1, fontFamily: "'Plus Jakarta Sans',sans-serif" }} />
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px 28px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {loadingProds ? (
            <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '32px 0' }}>Loading products…</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '40px 0' }}>No products found.</div>
          ) : filtered.map(p => {
            const isTagged = tagged.find(x => x.id === p.id)
            return (
              <div key={p.id} className={`product-row${isTagged ? ' tagged' : ''}`} onClick={() => onToggle(p)}>
                <div style={{ width: 48, height: 48, borderRadius: 12, background: '#f3f3f0', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, border: '1px solid #e9e9e4' }}>
                  {p.image ? <img src={p.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none' }} /> : '📦'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: '#7c3aed', fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>{Number(p.price).toLocaleString()} RWF</div>
                  {p.stock !== undefined && (
                    <div style={{ fontSize: 10, color: p.stock > 0 ? '#15803d' : '#b91c1c', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>
                      {p.stock > 0 ? `${p.stock} in stock` : 'Out of stock'}
                    </div>
                  )}
                </div>
                <div style={{ width: 26, height: 26, borderRadius: 8, border: `2px solid ${isTagged ? '#7c3aed' : '#d1d5db'}`, background: isTagged ? '#7c3aed' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#fff', fontSize: 13, fontWeight: 900 }}>
                  {isTagged ? '✓' : ''}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </>
  )
}

// ── Add Clip Drawer ───────────────────────────────────────────────────────────
function AddClipDrawer({ onClose, onSuccess, showPicker, setShowPicker, products, loadingProds, tagged, setTagged, pickerSearch, setPickerSearch, maxDurationSecs, reelsToday, maxReelsPerDay }) {
  const [step,           setStep]           = useState('form') // 'form' | 'uploading' | 'done'
  const [videoFile,      setVideoFile]      = useState(null)
  const [videoPreview,   setVideoPreview]   = useState(null)
  const [thumbFile,      setThumbFile]      = useState(null)
  const [thumbPreview,   setThumbPreview]   = useState(null)
  const [caption,        setCaption]        = useState('')
  const [progress,       setProgress]       = useState(0)
  const [progressLabel,  setProgressLabel]  = useState('')
  const [error,          setError]          = useState('')
  const [needsReview,    setNeedsReview]    = useState(false)
  const videoInputRef = useRef()
  const thumbInputRef = useRef()

  const onVideoChange = (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    const url = URL.createObjectURL(f)
    if (maxDurationSecs) {
      const vid = document.createElement('video')
      vid.preload = 'metadata'
      vid.onloadedmetadata = () => {
        if (vid.duration > maxDurationSecs) {
          URL.revokeObjectURL(url)
          setError(`Video is ${Math.round(vid.duration)}s — max allowed is ${maxDurationSecs}s.`)
          return
        }
        setError('')
        setVideoFile(f)
        setVideoPreview(url)
      }
      vid.src = url
    } else {
      setVideoFile(f)
      setVideoPreview(url)
    }
  }

  const onThumbChange = (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    setThumbFile(f)
    setThumbPreview(URL.createObjectURL(f))
  }

  const handlePublish = async () => {
    if (!videoFile) { setError('Please select a video file.'); return }
    if (!caption.trim()) { setError('Caption is required.'); return }
    setError('')
    setStep('uploading')
    setProgress(0)

    try {
      // ── Step 1: Upload video + thumbnail ─────────────────
      setProgressLabel('Uploading video to cloud…')
      const form = new FormData()
      form.append('video', videoFile)
      if (thumbFile) form.append('thumbnail', thumbFile)

      const uploadRes = await api.post('/reels/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 5 * 60 * 1000,
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 68))
        },
      })

      setProgress(75)
      setProgressLabel('Saving clip…')

      const { videoUrl, thumbnailUrl, needsReview: review } = uploadRes.data
      setNeedsReview(!!review)

      // Fake progress 75→95 while saving to DB
      const iv = setInterval(() => setProgress(p => p >= 95 ? (clearInterval(iv), 95) : p + Math.random() * 6), 200)

      // ── Step 2: Save reel record ──────────────────────────
      await api.post('/reels/add', {
        caption:      caption.trim(),
        videoUrl,
        thumbnailUrl: thumbnailUrl ?? null,
        prodId:       tagged.length > 0 ? tagged.map(p => p.id) : null,
        needsReview:  !!review,
      })

      clearInterval(iv)
      setProgress(100)
      setProgressLabel('Done!')
      setTimeout(() => setStep('done'), 350)

    } catch (err) {
      setStep('form')
      setProgress(0)
      setError(
        err?.response?.data?.details ??
        err?.response?.data?.error   ??
        err?.response?.data?.message ??
        'Upload failed. Please try again.'
      )
    }
  }

  const reset = () => {
    setStep('form'); setVideoFile(null); setVideoPreview(null)
    setThumbFile(null); setThumbPreview(null); setCaption('')
    setTagged([]); setProgress(0); setError(''); setNeedsReview(false)
  }

  return (
    <>
      <style>{`
        @keyframes slideInRight { from { transform:translateX(40px); opacity:0 } to { transform:translateX(0); opacity:1 } }
        @keyframes progressFill { from { width:0% } }
        .add-clip-overlay { position:fixed;inset:0;background:rgba(0,0,0,0.2);z-index:200 }
        .add-clip-drawer  { position:fixed;top:0;right:0;bottom:0;width:520px;background:#fff;z-index:201;display:flex;flex-direction:column;box-shadow:-4px 0 40px rgba(0,0,0,0.12);animation:slideInRight 0.22s ease;overflow:hidden }
        .upload-zone      { border:2px dashed #7c3aed55;border-radius:14px;padding:28px 20px;display:flex;flex-direction:column;align-items:center;gap:10;cursor:pointer;background:#faf5ff;transition:background 0.15s }
        .upload-zone:hover{ background:#f3e8ff }
        .upload-zone-set  { border:2px solid #7c3aed;border-radius:14px;padding:16px;background:#faf5ff }
        .tag-btn          { display:flex;align-items:center;gap:12;padding:14px 16px;border-radius:14px;border:1.5px solid #e9e9e4;background:#fff;cursor:pointer;width:100%;text-align:left;transition:border-color 0.15s }
        .tag-btn:hover    { border-color:#7c3aed55;background:#faf5ff }
        .product-row      { display:flex;align-items:center;gap:12;padding:10px 12px;border-radius:12px;border:1.5px solid #e9e9e4;background:#fff;cursor:pointer;transition:all 0.12s }
        .product-row:hover{ background:#faf5ff;border-color:#7c3aed55 }
        .product-row.tagged{ background:rgba(124,58,237,0.08);border-color:rgba(124,58,237,0.4) }
        .prog-track       { height:6px;border-radius:3px;background:#e9e9e4;overflow:hidden;margin:12px 0 }
        .prog-fill        { height:100%;border-radius:3px;background:#7c3aed;transition:width 0.25s ease }
        .tip-row          { display:flex;align-items:flex-start;gap:8;margin-bottom:7px }
      `}</style>

      <div className="add-clip-overlay" onClick={step === 'uploading' ? undefined : onClose} />
      <div className="add-clip-drawer">

        {/* ── Header ── */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(124,58,237,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🎬</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}>Create Clip</div>
            <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>Short product video · published instantly</div>
          </div>
          {step !== 'uploading' && (
            <div onClick={onClose} style={{ cursor: 'pointer', fontSize: 22, color: '#9a9a94', lineHeight: 1 }}>×</div>
          )}
        </div>

        {/* ── Body ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '22px 22px 0' }}>

          {/* ── DONE STATE ── */}
          {step === 'done' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '40px 0 20px', textAlign: 'center' }}>
              <div style={{ width: 88, height: 88, borderRadius: 26, background: 'rgba(124,58,237,0.1)', border: '1.5px solid rgba(124,58,237,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 44 }}>✅</div>
              <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>Clip Published!</div>
              <div style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6, maxWidth: 320 }}>
                {needsReview
                  ? 'Your clip is under review and will go live once approved.'
                  : 'Your clip is live and discoverable by all buyers on Kudos.'}
              </div>
              {tagged.length > 0 && (
                <div style={{ background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.25)', borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
                  <span style={{ fontSize: 18 }}>🛍</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#7c3aed' }}>
                    {tagged.length} product{tagged.length > 1 ? 's are' : ' is'} tagged — viewers can tap to buy instantly.
                  </span>
                </div>
              )}
              <button onClick={() => { reset(); onSuccess() }} style={{ width: '100%', height: 50, borderRadius: 14, border: 'none', background: '#7c3aed', color: '#fff', fontSize: 15, fontWeight: 800, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", marginTop: 8 }}>
                Done ✓
              </button>
              <button onClick={reset} style={{ width: '100%', height: 44, borderRadius: 12, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", color: '#6b7280' }}>
                + Add Another Clip
              </button>
            </div>
          )}

          {/* ── UPLOADING STATE ── */}
          {step === 'uploading' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '48px 0 20px', textAlign: 'center' }}>
              <div style={{ width: 80, height: 80, borderRadius: 24, background: 'rgba(124,58,237,0.1)', border: '1.5px solid rgba(124,58,237,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36 }}>☁️</div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>{progressLabel}</div>
              <div style={{ width: '100%', maxWidth: 340 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>{progress < 75 ? 'Uploading to cloud…' : 'Saving clip…'}</span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: '#7c3aed', fontFamily: "'JetBrains Mono',monospace" }}>{Math.round(progress)}%</span>
                </div>
                <div className="prog-track">
                  <div className="prog-fill" style={{ width: `${progress}%` }} />
                </div>
              </div>
              <div style={{ fontSize: 13, color: '#9a9a94' }}>Please don't close this window…</div>
            </div>
          )}

          {/* ── FORM STATE ── */}
          {step === 'form' && (
            <>
              {/* ── Daily cap warning ── */}
              {maxReelsPerDay > 0 && reelsToday >= maxReelsPerDay && (
                <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#92400e' }}>
                  ⚠ You've reached today's limit of {maxReelsPerDay} clip{maxReelsPerDay !== 1 ? 's' : ''}. New clips can be posted tomorrow.
                </div>
              )}
              {maxReelsPerDay > 0 && reelsToday < maxReelsPerDay && reelsToday >= maxReelsPerDay - 2 && (
                <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#166534' }}>
                  {maxReelsPerDay - reelsToday} clip{maxReelsPerDay - reelsToday !== 1 ? 's' : ''} remaining today (limit: {maxReelsPerDay}/day).
                </div>
              )}

              {/* ── Video file ── */}
              <div style={{ marginBottom: 22 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '1.1px', marginBottom: 10 }}>Video File</div>
                {videoFile ? (
                  <div className="upload-zone-set">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                      <div style={{ width: 44, height: 44, borderRadius: 10, background: 'rgba(124,58,237,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>🎥</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{videoFile.name}</div>
                        <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>
                          {(videoFile.size / 1024 / 1024).toFixed(1)} MB · Video ready
                        </div>
                      </div>
                      <span style={{ background: 'rgba(124,58,237,0.12)', color: '#7c3aed', fontSize: 9, fontWeight: 800, padding: '3px 8px', borderRadius: 6, border: '1px solid rgba(124,58,237,0.3)', fontFamily: "'JetBrains Mono',monospace" }}>VID</span>
                    </div>
                    {videoPreview && (
                      <video src={videoPreview} controls style={{ width: '100%', borderRadius: 10, maxHeight: 200, background: '#111' }} />
                    )}
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <button onClick={() => videoInputRef.current?.click()} style={{ flex: 1, height: 36, borderRadius: 9, border: '1px solid #e9e9e4', background: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", color: '#6b7280' }}>↺ Change</button>
                      <button onClick={() => { setVideoFile(null); setVideoPreview(null) }} style={{ flex: 1, height: 36, borderRadius: 9, border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>✕ Remove</button>
                    </div>
                  </div>
                ) : (
                  <div className="upload-zone" onClick={() => videoInputRef.current?.click()}>
                    <div style={{ width: 60, height: 60, borderRadius: 18, background: 'rgba(124,58,237,0.12)', border: '1.5px solid rgba(124,58,237,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>🎥</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#7c3aed' }}>Click to pick a video</div>
                    <div style={{ fontSize: 11, color: '#9a9a94', textAlign: 'center' }}>MP4 · MOV · Max {maxDurationSecs ?? 60}s · up to 300 MB</div>
                  </div>
                )}
                <input ref={videoInputRef} type="file" accept="video/*" style={{ display: 'none' }} onChange={onVideoChange} />
              </div>

              {/* ── Thumbnail ── */}
              <div style={{ marginBottom: 22 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '1.1px', marginBottom: 10 }}>
                  Thumbnail <span style={{ fontSize: 9, fontWeight: 800, background: 'rgba(124,58,237,0.1)', color: '#7c3aed', padding: '2px 7px', borderRadius: 6, border: '1px solid rgba(124,58,237,0.25)', marginLeft: 6 }}>REQUIRED</span>
                </div>
                {thumbFile ? (
                  <div className="upload-zone-set" style={{ padding: 0, overflow: 'hidden' }}>
                    <div style={{ position: 'relative' }}>
                      <img src={thumbPreview} alt="thumbnail" style={{ width: '100%', maxHeight: 200, objectFit: 'cover', display: 'block' }} />
                      <div style={{ position: 'absolute', top: 8, left: 8, background: '#7c3aed', color: '#fff', fontSize: 9, fontWeight: 800, padding: '3px 8px', borderRadius: 6 }}>Cover</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, padding: 12 }}>
                      <button onClick={() => thumbInputRef.current?.click()} style={{ flex: 1, height: 34, borderRadius: 9, border: '1px solid #e9e9e4', background: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", color: '#6b7280' }}>↺ Change</button>
                      <button onClick={() => { setThumbFile(null); setThumbPreview(null) }} style={{ flex: 1, height: 34, borderRadius: 9, border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>✕ Remove</button>
                    </div>
                  </div>
                ) : (
                  <div className="upload-zone" onClick={() => thumbInputRef.current?.click()} style={{ padding: '20px' }}>
                    <div style={{ width: 52, height: 52, borderRadius: 16, background: 'rgba(124,58,237,0.12)', border: '1.5px solid rgba(124,58,237,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>🖼</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#7c3aed' }}>Set a cover image</div>
                    <div style={{ fontSize: 11, color: '#9a9a94' }}>JPG · PNG · 9:16 ratio recommended</div>
                  </div>
                )}
                <input ref={thumbInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onThumbChange} />
              </div>

              {/* ── Caption ── */}
              <div style={{ marginBottom: 22 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '1.1px', marginBottom: 10 }}>Caption</div>
                <div style={{ border: `1.5px solid ${caption.length > 0 ? '#7c3aed' : '#e9e9e4'}`, borderRadius: 14, padding: '12px 14px', background: '#fff', transition: 'border-color 0.15s' }}>
                  <textarea
                    value={caption}
                    onChange={e => setCaption(e.target.value)}
                    maxLength={300}
                    rows={3}
                    placeholder="Describe your product, add a price or CTA…"
                    style={{ width: '100%', border: 'none', outline: 'none', resize: 'none', fontSize: 14, color: '#111317', fontFamily: "'Plus Jakarta Sans',sans-serif", lineHeight: 1.6, background: 'transparent' }}
                  />
                  <div style={{ textAlign: 'right', fontSize: 11, color: caption.length > 260 ? '#b91c1c' : '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 4 }}>
                    {caption.length}/300
                  </div>
                </div>
              </div>

              {/* ── Tag Products ── */}
              <div style={{ marginBottom: 22 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '1.1px' }}>Tag Products</div>
                  <span style={{ fontSize: 9, fontWeight: 800, background: 'rgba(124,58,237,0.1)', color: '#7c3aed', padding: '2px 8px', borderRadius: 6, border: '1px solid rgba(124,58,237,0.25)' }}>
                    Let viewers buy instantly
                  </span>
                </div>

                {/* Tagged chips */}
                {tagged.length > 0 && (
                  <div style={{ background: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.2)', borderRadius: 12, padding: '12px 14px', marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 10 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#111317' }}>{tagged.length} product{tagged.length > 1 ? 's' : ''} tagged</span>
                      <button onClick={() => setShowPicker(true)} style={{ fontSize: 11, fontWeight: 800, color: '#7c3aed', background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.3)', padding: '3px 10px', borderRadius: 7, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Edit</button>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                      {tagged.map(p => (
                        <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 7, background: '#fff', border: '1.5px solid rgba(124,58,237,0.3)', borderRadius: 10, padding: '6px 10px 6px 8px' }}>
                          {p.image
                            ? <img src={p.image} alt="" style={{ width: 28, height: 28, borderRadius: 7, objectFit: 'cover' }} />
                            : <span style={{ fontSize: 18 }}>📦</span>}
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 700, maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                            <div style={{ fontSize: 10, color: '#7c3aed', fontWeight: 800, fontFamily: "'JetBrains Mono',monospace" }}>{Number(p.price).toLocaleString()} RWF</div>
                          </div>
                          <button onClick={() => setTagged(prev => prev.filter(x => x.id !== p.id))} style={{ width: 20, height: 20, borderRadius: 5, background: '#fef2f2', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#b91c1c', fontSize: 12, fontWeight: 900, padding: 0, lineHeight: 1 }}>×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button className="tag-btn" onClick={() => setShowPicker(true)}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(124,58,237,0.08)', border: '1.5px solid rgba(124,58,237,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>🛍</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: tagged.length > 0 ? '#7c3aed' : '#111317', marginBottom: 2 }}>
                      {tagged.length > 0 ? `${tagged.length} product${tagged.length > 1 ? 's' : ''} tagged` : 'Add Products to this Clip'}
                    </div>
                    <div style={{ fontSize: 11, color: '#9a9a94' }}>
                      {tagged.length > 0 ? 'Tap to add or remove products' : 'Viewers can tap products while watching to buy'}
                    </div>
                  </div>
                  <span style={{ fontSize: 18, color: tagged.length > 0 ? '#7c3aed' : '#9a9a94' }}>→</span>
                </button>
              </div>

              {/* ── Tips ── */}
              <div style={{ background: 'rgba(124,58,237,0.05)', border: '1px solid rgba(124,58,237,0.15)', borderRadius: 14, padding: '14px 16px', marginBottom: 22 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', letterSpacing: '0.3px', marginBottom: 10 }}>💡 Tips for great Clips</div>
                {TIPS.map((tip, i) => (
                  <div key={i} className="tip-row">
                    <div style={{ width: 5, height: 5, borderRadius: 3, background: '#7c3aed', marginTop: 6, flexShrink: 0 }} />
                    <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.6 }}>{tip}</div>
                  </div>
                ))}
              </div>

              {error && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#b91c1c' }}>⚠ {error}</div>
              )}
            </>
          )}
        </div>

        {/* ── Footer publish button ── */}
        {step === 'form' && (
          <div style={{ padding: '16px 22px 22px', borderTop: '1px solid #e9e9e4', flexShrink: 0 }}>
            <button
              onClick={handlePublish}
              disabled={maxReelsPerDay > 0 && reelsToday >= maxReelsPerDay}
              style={{ width: '100%', height: 52, borderRadius: 14, border: 'none', background: (maxReelsPerDay > 0 && reelsToday >= maxReelsPerDay) ? '#c4b5fd' : '#7c3aed', color: '#fff', fontSize: 15, fontWeight: 800, cursor: (maxReelsPerDay > 0 && reelsToday >= maxReelsPerDay) ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, boxShadow: '0 4px 14px rgba(124,58,237,0.35)' }}>
              <span style={{ fontSize: 20 }}>☁️</span>
              Publish Clip
              {tagged.length > 0 && (
                <span style={{ background: 'rgba(255,255,255,0.2)', fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 7 }}>
                  + {tagged.length} product{tagged.length > 1 ? 's' : ''}
                </span>
              )}
            </button>
            {(videoFile || caption) && (
              <button onClick={reset} style={{ width: '100%', height: 36, marginTop: 8, borderRadius: 10, border: 'none', background: 'transparent', color: '#9a9a94', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                ↺ Clear & start over
              </button>
            )}
          </div>
        )}
      </div>

    </>
  )
}

// ── View Detail Drawer ────────────────────────────────────────────────────────
function ReelDrawer({ reel, onClose, onDelete, onToggleStatus }) {
  const [tab,        setTab]        = useState('details')
  const [detail,     setDetail]     = useState(null)
  const [detailLoad, setDetailLoad] = useState(false)

  useEffect(() => {
    if (!reel) return
    setTab('details'); setDetail(null); setDetailLoad(true)
    const id = reel.reel_id || reel.reels_id
    api.get(`/reels/seller/reel/${id}`)
      .then(res => setDetail(res.data.reel))
      .catch(() => setDetail(reel))
      .finally(() => setDetailLoad(false))
  }, [reel])

  if (!reel) return null
  const d     = detail || reel
  const id    = d.reel_id || d.reels_id
  const thumb = parseThumb(d.thumbnail_url || d.thumbnail || d.video_url)

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.2)', zIndex: 200 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 500, background: '#fff', zIndex: 201, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 40px rgba(0,0,0,0.12)', animation: 'slideInRight 0.22s ease' }}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 48, height: 64, borderRadius: 8, background: '#111317', flexShrink: 0, overflow: 'hidden' }}>
            {thumb
              ? <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display = 'none'} />
              : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.4)', fontSize: 20 }}>▶</div>
            }
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {d.title || d.caption || `Clip #${id}`}
            </div>
            <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>#{id} · {fmt(d.created_at)}</div>
          </div>
          <StatusBadge status={d.status} />
          <div onClick={onClose} style={{ cursor: 'pointer', fontSize: 20, color: '#9a9a94', marginLeft: 6 }}>×</div>
        </div>

        {/* Engagement strip */}
        <div style={{ display: 'flex', padding: '10px 20px', borderBottom: '1px solid #e9e9e4', gap: 20 }}>
          {[['❤️', num(d.like_count ?? d.likes), 'Likes'], ['💬', num(d.comment_count), 'Comments'], ['🛍', num(d.product_count ?? d.products?.length ?? 0), 'Products']].map(([icon, val, lbl]) => (
            <div key={lbl} style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ fontSize: 16 }}>{icon}</div>
              <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.4px' }}>{val}</div>
              <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.06em' }}>{lbl}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e9e9e4', padding: '0 20px' }}>
          {[['details','Details'],['products','Products'],['comments','Comments']].map(([tid, label]) => (
            <button key={tid} onClick={() => setTab(tid)} style={{ padding: '10px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif", color: tab === tid ? '#111317' : '#9a9a94', borderBottom: `2px solid ${tab === tid ? '#e8440a' : 'transparent'}`, marginBottom: -1 }}>
              {label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {detailLoad && <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '32px 0' }}>Loading…</div>}

          {!detailLoad && tab === 'details' && (
            <>
              {(d.video_url || d.thumbnail_url) && (
                <div style={{ background: '#111317', borderRadius: 10, overflow: 'hidden', marginBottom: 16, maxHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {d.video_url
                    ? <video src={d.video_url} controls style={{ width: '100%', maxHeight: 240, objectFit: 'cover' }} />
                    : thumb ? <img src={thumb} alt="" style={{ width: '100%', maxHeight: 240, objectFit: 'cover' }} /> : <span style={{ color: '#fff', fontSize: 32 }}>▶</span>
                  }
                </div>
              )}
              {d.caption && <div style={{ background: '#f9f9f7', borderRadius: 8, padding: '12px 14px', marginBottom: 14, fontSize: 13, color: '#374151', lineHeight: 1.6 }}>{d.caption}</div>}
              {[['Clip ID', id], ['Status', <StatusBadge status={d.status} />], ['Likes', num(d.like_count ?? d.likes)], ['Comments', num(d.comment_count)], ['Products', num(d.product_count ?? d.products?.length ?? 0)], ['Posted', fmtTime(d.created_at)]].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid #f3f3f0' }}>
                  <span style={{ fontSize: 12, color: '#9a9a94', fontWeight: 600 }}>{k}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, fontFamily: typeof v === 'string' ? "'JetBrains Mono',monospace" : 'inherit', color: '#111317', maxWidth: 260, textAlign: 'right' }}>{v}</span>
                </div>
              ))}
            </>
          )}

          {!detailLoad && tab === 'products' && (
            !d.products?.length
              ? <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '40px 0' }}>No products tagged in this clip.</div>
              : d.products.map((p, i) => {
                  let imgUrl = null
                  try { const imgs = typeof p.images === 'string' ? JSON.parse(p.images) : p.images; imgUrl = parseThumb(Array.isArray(imgs) ? imgs[0] : p.images) } catch { imgUrl = parseThumb(p.images) }
                  return (
                    <div key={p.prod_id || i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f3f3f0' }}>
                      <div style={{ width: 46, height: 46, borderRadius: 8, background: '#f3f3f0', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                        {imgUrl ? <img src={imgUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none' }} /> : '📦'}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                        <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>{p.category || '—'}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace" }}>{p.price != null ? `${Number(p.price).toLocaleString()} RWF` : '—'}</div>
                        <div style={{ fontSize: 10, color: p.quantity > 0 ? '#15803d' : '#b91c1c', fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>{p.quantity > 0 ? `${p.quantity} in stock` : 'Out of stock'}</div>
                      </div>
                    </div>
                  )
                })
          )}

          {!detailLoad && tab === 'comments' && (() => {
            const comments = d.comments || []
            if (!comments.length) return <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '40px 0' }}>No comments yet.</div>
            const avatarBg = (str = '') => { const c = ['#fde8d8','#dff5e8','#e8f4ff','#fef3d0','#ede8ff','#fce7f3']; let h = 0; for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h); return c[Math.abs(h) % c.length] }
            const initials = (n = '') => (n.trim().charAt(0) || '?').toUpperCase()
            const renderComment = (c, isReply = false) => (
              <div key={c.comment_id} style={{ padding: isReply ? '8px 0' : '12px 0', borderBottom: isReply ? 'none' : '1px solid #f3f3f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <div style={{ width: isReply ? 22 : 28, height: isReply ? 22 : 28, borderRadius: 6, background: avatarBg(c.username || ''), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: isReply ? 9 : 11, fontWeight: 800, flexShrink: 0 }}>{initials(c.username)}</div>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: isReply ? 11 : 12, fontWeight: 700 }}>{c.username || 'Anonymous'}</span>
                    <span style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginLeft: 8 }}>{fmtTime(c.created_at)}</span>
                  </div>
                </div>
                <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, paddingLeft: isReply ? 30 : 36 }}>{c.comment || c.content}</div>
              </div>
            )
            const topLevel = comments.filter(c => !c.parent_id)
            const replies  = comments.filter(c => !!c.parent_id)
            return topLevel.map(parent => (
              <div key={parent.comment_id}>
                {renderComment(parent)}
                {replies.filter(r => r.parent_id === parent.comment_id).map(reply => (
                  <div key={reply.comment_id} style={{ display: 'flex', paddingLeft: 28 }}>
                    <div style={{ width: 2, background: '#e5e7eb', borderRadius: 2, marginRight: 10, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>{renderComment(reply, true)}</div>
                  </div>
                ))}
              </div>
            ))
          })()}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid #e9e9e4', display: 'flex', gap: 10 }}>
          <button onClick={() => onToggleStatus(d)} style={{ flex: 1, height: 40, borderRadius: 8, border: 'none', cursor: 'pointer', background: d.status === 1 ? '#fef2f2' : '#f0fdf4', color: d.status === 1 ? '#b91c1c' : '#15803d', fontSize: 12, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            {d.status === 1 ? 'Hide Clip' : 'Restore Clip'}
          </button>
          <button onClick={() => { onDelete(d); onClose() }} style={{ flex: 1, height: 40, borderRadius: 8, border: '1px solid #fecaca', cursor: 'pointer', background: '#fff5f5', color: '#b91c1c', fontSize: 12, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            Delete Clip
          </button>
        </div>
      </div>
    </>
  )
}

function PagBtn({ label, onClick, disabled, active }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ minWidth: 34, height: 34, borderRadius: 7, border: '1px solid #e9e9e4', background: active ? '#111317' : '#fff', color: active ? '#fff' : disabled ? '#9a9a94' : '#111317', fontSize: 12, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', padding: '0 10px', fontFamily: "'Plus Jakarta Sans',sans-serif", opacity: disabled ? 0.4 : 1 }}>
      {label}
    </button>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function SellerReels() {
  const [reels,        setReels]        = useState([])
  const [loading,      setLoading]      = useState(true)
  const [error,        setError]        = useState('')
  const [search,       setSearch]       = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [page,         setPage]         = useState(1)
  const [totalPages,   setTotalPages]   = useState(1)
  const [totalCount,   setTotalCount]   = useState(0)
  const [stats,        setStats]        = useState({ total: 0, active: 0, hidden: 0, total_likes: 0, total_comments: 0 })
  const [selected,     setSelected]     = useState(null)
  const [deleting,     setDeleting]     = useState(null)
  const [deleteLoad,   setDeleteLoad]   = useState(false)
  const [view,         setView]         = useState('grid')
  const [showAdd,      setShowAdd]      = useState(false)

  // Picker state lives here so ProductPickerPortal renders outside any
  // position:fixed stacking context (drawer creates a new one)
  const [showPicker,   setShowPicker]   = useState(false)
  const [pickerSearch, setPickerSearch] = useState('')
  const [products,     setProducts]     = useState([])
  const [loadingProds, setLoadingProds] = useState(false)
  const [tagged,       setTagged]       = useState([])
  const [config,       setConfig]       = useState({ reel_max_duration_secs: 60, max_reels_per_day: 10 })
  const [reelsToday,   setReelsToday]   = useState(0)

  const PER_PAGE = 18

  // Fetch platform config once on mount
  useEffect(() => {
    api.get('/settings/config')
      .then(res => {
        const c = res.data?.config || res.data || {}
        setConfig(prev => ({
          ...prev,
          ...(c.reel_max_duration_secs != null && { reel_max_duration_secs: Number(c.reel_max_duration_secs) }),
          ...(c.max_reels_per_day      != null && { max_reels_per_day:      Number(c.max_reels_per_day) }),
        }))
      })
      .catch(() => {}) // silently fall back to defaults
  }, [])

  // Fetch shop products once on mount for the tag-products picker
  useEffect(() => {
    setLoadingProds(true)
    api.get('/products/my-products')
      .then(res => {
        const raw = res.data?.products ?? res.data ?? []
        setProducts(raw.map(p => {
          let image = null
          try {
            const imgs = typeof p.images === 'string' ? JSON.parse(p.images) : p.images
            if (Array.isArray(imgs) && imgs.length > 0) {
              image = imgs[0].startsWith('http') ? imgs[0] : `${API_BASE}/${imgs[0].split('/').pop()}`
            }
          } catch {}
          return { id: String(p.prod_id ?? p.id), name: p.name, price: p.price, image, stock: p.quantity }
        }))
      })
      .catch(() => {})
      .finally(() => setLoadingProds(false))
  }, [])

  const fetchReels = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const res = await api.get('/reels/seller/my-reels')
      const all = (res.data.reels || []).map(r => ({ ...r, reel_id: r.reel_id || r.reels_id }))
      const filtered = all.filter(r => {
        const matchSearch = !search || [r.caption, r.title, String(r.reel_id)].some(f => f?.toLowerCase().includes(search.toLowerCase()))
        const matchStatus = statusFilter === '' || String(r.status) === String(statusFilter)
        return matchSearch && matchStatus
      })
      setStats({
        total:          all.length,
        active:         all.filter(r => r.status === 1).length,
        hidden:         all.filter(r => r.status === 0).length,
        total_likes:    all.reduce((s, r) => s + Number(r.like_count || r.likes || 0), 0),
        total_comments: all.reduce((s, r) => s + Number(r.comment_count || 0), 0),
      })
      const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0)
      setReelsToday(all.filter(r => r.created_at && new Date(r.created_at) >= todayStart).length)
      const pages = Math.max(1, Math.ceil(filtered.length / PER_PAGE))
      setReels(filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE))
      setTotalPages(pages)
      setTotalCount(filtered.length)
    } catch { setError('Failed to load your clips.') }
    finally { setLoading(false) }
  }, [page, search, statusFilter])

  useEffect(() => { fetchReels() }, [fetchReels])
  useEffect(() => { setPage(1) },   [search, statusFilter])

  const handleToggleStatus = async (reel) => {
    const id = reel.reel_id || reel.reels_id
    const newStatus = reel.status === 1 ? 0 : 1
    try {
      await api.patch(`/reels/seller/${id}/status`, { status: newStatus })
      setReels(prev => prev.map(r => (r.reel_id || r.reels_id) === id ? { ...r, status: newStatus } : r))
      if (selected && (selected.reel_id || selected.reels_id) === id) setSelected(prev => ({ ...prev, status: newStatus }))
    } catch { alert('Failed to update clip status') }
  }

  const handleDelete = async () => {
    if (!deleting) return
    setDeleteLoad(true)
    const id = deleting.reel_id || deleting.reels_id
    try {
      await api.delete(`/reels/delete/${id}`)
      setReels(prev => prev.filter(r => (r.reel_id || r.reels_id) !== id))
      setTotalCount(c => c - 1)
      setDeleting(null); setSelected(null)
    } catch (err) { alert(err.response?.data?.error || 'Failed to delete clip') }
    finally { setDeleteLoad(false) }
  }

  return (
    <div style={{ padding: '28px 32px 60px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <style>{`@keyframes slideInRight { from { transform:translateX(40px);opacity:0 } to { transform:translateX(0);opacity:1 } }`}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>My Clips</div>
          <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>Manage the clips posted from your shop.</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {/* View toggle */}
          <div style={{ display: 'flex', background: '#fff', border: '1px solid #e9e9e4', borderRadius: 8, padding: 4, gap: 2 }}>
            {[['grid','⊞'],['list','☰']].map(([v, icon]) => (
              <button key={v} onClick={() => setView(v)} style={{ width: 34, height: 30, borderRadius: 6, border: 'none', background: view === v ? '#111317' : 'transparent', color: view === v ? '#fff' : '#9a9a94', fontSize: 14, cursor: 'pointer' }}>{icon}</button>
            ))}
          </div>
          {/* ── Add Clip button ── */}
          <button
            onClick={() => setShowAdd(true)}
            style={{ height: 38, padding: '0 18px', borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', gap: 7, boxShadow: '0 2px 10px rgba(124,58,237,0.3)' }}>
            🎬 Add Clip
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 24 }}>
        <StatCard label="Total Clips"    value={stats.total.toLocaleString()}                       />
        <StatCard label="Active"         value={stats.active.toLocaleString()}  accent="#15803d"    />
        <StatCard label="Hidden"         value={stats.hidden.toLocaleString()}  accent="#b91c1c"    />
        <StatCard label="Total Likes"    value={num(stats.total_likes)}         accent="#e8440a"    />
        <StatCard label="Total Comments" value={num(stats.total_comments)}      accent="#1d4ed8"    />
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, background: '#f6f6f3', border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 12px' }}>
          <span style={{ fontSize: 14, color: '#9a9a94' }}>🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by caption or clip ID…" style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: '#111317', flex: 1, fontFamily: "'Plus Jakarta Sans',sans-serif" }} />
          {search && <span onClick={() => setSearch('')} style={{ cursor: 'pointer', color: '#9a9a94', fontSize: 16 }}>×</span>}
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ border: '1px solid #e9e9e4', borderRadius: 7, padding: '8px 12px', fontSize: 12, fontWeight: 600, color: '#111317', background: '#fff', cursor: 'pointer', outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
          <option value="">All Status</option>
          <option value="1">Active</option>
          <option value="0">Hidden</option>
          <option value="2">Pending</option>
        </select>
        <button onClick={fetchReels} style={{ height: 36, width: 36, borderRadius: 7, border: '1px solid #e9e9e4', background: '#fff', cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>↻</button>
        <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap' }}>{totalCount.toLocaleString()} clips</div>
      </div>

      {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px', marginBottom: 14, fontSize: 13, color: '#b91c1c' }}>⚠ {error}</div>}

      {loading ? (
        <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '60px 0' }}>Loading your clips…</div>
      ) : reels.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🎬</div>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>No clips yet</div>
          <div style={{ fontSize: 13, color: '#9a9a94', marginBottom: 20 }}>Clips you post from your shop will appear here.</div>
          <button onClick={() => setShowAdd(true)} style={{ height: 44, padding: '0 24px', borderRadius: 10, border: 'none', background: '#7c3aed', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", boxShadow: '0 2px 10px rgba(124,58,237,0.3)' }}>
            🎬 Create your first clip
          </button>
        </div>
      ) : view === 'grid' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14, marginBottom: 20 }}>
          {reels.map(reel => {
            const thumb = parseThumb(reel.thumbnail_url || reel.thumbnail || reel.video_url)
            const id    = reel.reel_id || reel.reels_id
            return (
              <div key={id} onClick={() => setSelected(reel)}
                style={{ background: '#111317', borderRadius: 10, overflow: 'hidden', cursor: 'pointer', position: 'relative', aspectRatio: '9/14', transition: 'transform 0.15s', border: `2px solid ${reel.status === 0 ? 'rgba(239,68,68,0.5)' : reel.status === 2 ? 'rgba(245,158,11,0.5)' : 'transparent'}` }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.02)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
                {thumb ? <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} onError={e => e.target.style.display = 'none'} /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, color: 'rgba(255,255,255,0.3)' }}>▶</div>}
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 60%)', padding: '12px 10px 10px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 4 }}>
                    {reel.caption ? reel.caption.slice(0, 40) : `Clip #${id}`}
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)' }}>❤️ {num(reel.like_count || reel.likes)}</span>
                    <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)' }}>💬 {num(reel.comment_count)}</span>
                  </div>
                </div>
                {reel.status !== 1 && <div style={{ position: 'absolute', top: 8, left: 8, background: reel.status === 0 ? '#ef4444' : '#f59e0b', color: '#fff', fontSize: 9, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", padding: '2px 6px', borderRadius: 3, textTransform: 'uppercase' }}>{reel.status === 0 ? 'Hidden' : 'Pending'}</div>}
                {(reel.product_count > 0 || reel.products?.length > 0) && <div style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 9, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", padding: '2px 6px', borderRadius: 3 }}>🛍 {reel.product_count || reel.products?.length}</div>}
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: 0, transition: 'opacity 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '0'}
                  onClick={e => e.stopPropagation()}>
                  <button onClick={() => setSelected(reel)} style={{ padding: '6px 12px', borderRadius: 6, border: 'none', background: '#fff', color: '#111317', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>View</button>
                  <button onClick={() => setDeleting(reel)} style={{ padding: '6px 12px', borderRadius: 6, border: 'none', background: '#ef4444', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Delete</button>
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2.4fr 1fr 0.7fr 0.6fr 0.8fr 0.9fr 0.7fr', padding: '10px 20px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
            {['Clip','Date','Likes','Comments','Products','Status','Actions'].map(h => (
              <div key={h} style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
            ))}
          </div>
          {reels.map((reel, i) => {
            const thumb = parseThumb(reel.thumbnail_url || reel.thumbnail || reel.video_url)
            const id    = reel.reel_id || reel.reels_id
            return (
              <div key={id} onClick={() => setSelected(reel)}
                style={{ display: 'grid', gridTemplateColumns: '2.4fr 1fr 0.7fr 0.6fr 0.8fr 0.9fr 0.7fr', padding: '12px 20px', borderBottom: i < reels.length - 1 ? '1px solid #f3f3f0' : 'none', cursor: 'pointer', transition: 'background 0.12s', alignItems: 'center' }}
                onMouseEnter={e => e.currentTarget.style.background = '#fafaf8'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 36, height: 48, borderRadius: 6, background: '#111317', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {thumb ? <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display = 'none'} /> : <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14 }}>▶</span>}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{reel.caption ? reel.caption.slice(0, 50) : `Clip #${id}`}</div>
                    <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>#{id}</div>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{fmt(reel.created_at)}</div>
                <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>{num(reel.like_count || reel.likes)}</div>
                <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>{num(reel.comment_count)}</div>
                <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>{(reel.product_count > 0 || reel.products?.length > 0) ? <span>🛍 {reel.product_count || reel.products?.length}</span> : <span style={{ color: '#9a9a94' }}>—</span>}</div>
                <div><StatusBadge status={reel.status} /></div>
                <div style={{ display: 'flex', gap: 5 }} onClick={e => e.stopPropagation()}>
                  <button onClick={() => setSelected(reel)} style={{ padding: '4px 9px', borderRadius: 6, border: '1px solid #e9e9e4', background: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>View</button>
                  <button onClick={() => setDeleting(reel)} style={{ padding: '4px 9px', borderRadius: 6, border: 'none', background: '#fef2f2', color: '#b91c1c', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Del</button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
          <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>Page {page} of {totalPages}</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <PagBtn label="← Prev" disabled={page === 1}          onClick={() => setPage(p => p - 1)} />
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => { const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i; return <PagBtn key={p} label={p} active={p === page} onClick={() => setPage(p)} /> })}
            <PagBtn label="Next →" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} />
          </div>
        </div>
      )}

      {/* View drawer */}
      <ReelDrawer reel={selected} onClose={() => setSelected(null)} onDelete={r => setDeleting(r)} onToggleStatus={handleToggleStatus} />

      {/* Delete modal */}
      <DeleteModal reel={deleting} onClose={() => setDeleting(null)} onConfirm={handleDelete} loading={deleteLoad} />

      {/* Add clip drawer */}
      {showAdd && (
        <AddClipDrawer
          onClose={() => { setShowAdd(false); setTagged([]); setPickerSearch('') }}
          onSuccess={() => { setShowAdd(false); setTagged([]); setPickerSearch(''); fetchReels() }}
          showPicker={showPicker}
          setShowPicker={setShowPicker}
          products={products}
          loadingProds={loadingProds}
          tagged={tagged}
          setTagged={setTagged}
          pickerSearch={pickerSearch}
          setPickerSearch={setPickerSearch}
          maxDurationSecs={config.reel_max_duration_secs}
          reelsToday={reelsToday}
          maxReelsPerDay={config.max_reels_per_day}
        />
      )}

      {/* Product picker portal — sibling to drawer, NOT inside it,
          so position:fixed resolves to the viewport, not the drawer */}
      <ProductPickerPortal
        visible={showPicker}
        products={products}
        tagged={tagged}
        loadingProds={loadingProds}
        pickerSearch={pickerSearch}
        setPickerSearch={setPickerSearch}
        onToggle={p => {
          const pid = String(p.id)
          setTagged(prev => prev.find(x => x.id === pid) ? prev.filter(x => x.id !== pid) : [...prev, { ...p, id: pid }])
        }}
        onClose={() => setShowPicker(false)}
      />
    </div>
  )
}